import { motion } from 'framer-motion'

const MONTHS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']

type Props = {
  // 12 normalized values 0..1 representing demand by month
  curve: number[]
  peakStart: number // index 0..11
  peakEnd: number
  peakLabel: string
  peakMonths: string
  multiplier: string
  kpi1: { label: string; value: string }
  kpi2: { label: string; value: string }
}

const W = 600
const H = 320
const CHART_TOP = 90
const CHART_BOTTOM = 250
const CHART_LEFT = 50
const CHART_RIGHT = 560

function pointFor(i: number, v: number) {
  const x = CHART_LEFT + (i / 11) * (CHART_RIGHT - CHART_LEFT)
  const y = CHART_BOTTOM - v * (CHART_BOTTOM - CHART_TOP)
  return { x, y }
}

export default function SectorSeasonalAnimation({
  curve,
  peakStart,
  peakEnd,
  peakLabel,
  peakMonths,
  multiplier,
  kpi1,
  kpi2,
}: Props) {
  const points = curve.map((v, i) => pointFor(i, v))

  // Smooth path through points using simple line segments
  const linePath = points
    .map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
    .join(' ')

  const areaPath =
    `M ${points[0].x} ${CHART_BOTTOM} ` +
    points.map((p) => `L ${p.x} ${p.y}`).join(' ') +
    ` L ${points[points.length - 1].x} ${CHART_BOTTOM} Z`

  const peakStartX = pointFor(peakStart, 0).x
  const peakEndX = pointFor(peakEnd, 0).x

  return (
    <div className="relative aspect-[16/10] w-full rounded-2xl sm:rounded-3xl border border-ink-100 bg-gradient-to-br from-white via-accent-50/30 to-white overflow-hidden">
      {/* dot pattern */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: 'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
        }}
      />

      <motion.div
        aria-hidden
        animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -left-20 -top-16 w-72 h-72 rounded-full blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.18), transparent 70%)' }}
      />

      {/* Top header */}
      <div className="relative px-5 sm:px-7 pt-5 sm:pt-7 flex items-start justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-2.5 py-1 text-[10px] sm:text-xs text-accent-800">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-600" />
            </span>
            <span className="font-medium">{peakLabel}</span>
          </span>
          <p className="mt-2 text-[11px] sm:text-xs text-ink-700 font-medium">
            Charge mensuelle estimée
          </p>
          <p className="mt-0.5 text-[10px] sm:text-[11px] text-ink-500">
            {peakMonths}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.18em] font-semibold text-accent-700">
            Pic relatif
          </p>
          <p className="font-semibold tracking-[-0.04em] leading-none text-[clamp(1.6rem,3.5vw,2.4rem)] bg-gradient-to-br from-ink-950 via-accent-700 to-accent-500 bg-clip-text text-transparent">
            {multiplier}
          </p>
          <p className="mt-1 text-[10px] sm:text-[11px] text-ink-500">
            vs creux annuel
          </p>
        </div>
      </div>

      {/* Chart SVG */}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="absolute inset-0 w-full h-full pointer-events-none"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="seasonalArea" x1="0%" x2="0%" y1="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(59,93,255,0.45)" />
            <stop offset="100%" stopColor="rgba(59,93,255,0)" />
          </linearGradient>
        </defs>

        {/* Peak band */}
        <motion.rect
          x={peakStartX - 12}
          y={CHART_TOP - 10}
          width={peakEndX - peakStartX + 24}
          height={CHART_BOTTOM - CHART_TOP + 20}
          fill="rgba(59,93,255,0.07)"
          stroke="rgba(59,93,255,0.25)"
          strokeWidth="1"
          strokeDasharray="3 3"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.8, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
        />

        {/* Peak label */}
        <motion.text
          x={(peakStartX + peakEndX) / 2}
          y={CHART_TOP - 16}
          textAnchor="middle"
          fontSize="10"
          fontWeight="600"
          fill="#3b5dff"
          initial={{ opacity: 0, y: CHART_TOP - 8 }}
          whileInView={{ opacity: 1, y: CHART_TOP - 16 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 1.7 }}
        >
          PIC
        </motion.text>

        {/* Area fill */}
        <motion.path
          d={areaPath}
          fill="url(#seasonalArea)"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 1, delay: 1.2 }}
        />

        {/* Line */}
        <motion.path
          d={linePath}
          fill="none"
          stroke="#3b5dff"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 1.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        />

        {/* Highlight dots on peak months */}
        {points.map((p, i) =>
          i >= peakStart && i <= peakEnd ? (
            <motion.circle
              key={`peak-${i}`}
              cx={p.x}
              cy={p.y}
              r="3.5"
              fill="white"
              stroke="#3b5dff"
              strokeWidth="2"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 1.8 + (i - peakStart) * 0.08 }}
            />
          ) : null,
        )}

        {/* Month labels */}
        {MONTHS.map((m, i) => {
          const x = pointFor(i, 0).x
          const inPeak = i >= peakStart && i <= peakEnd
          return (
            <motion.text
              key={m + i}
              x={x}
              y={CHART_BOTTOM + 18}
              textAnchor="middle"
              fontSize="10"
              fontWeight={inPeak ? 700 : 500}
              fill={inPeak ? '#3b5dff' : '#9b9ba5'}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.6 + i * 0.04 }}
            >
              {m}
            </motion.text>
          )
        })}
      </svg>

      {/* KPI overlays */}
      <div className="absolute inset-x-0 bottom-5 sm:bottom-7 px-5 sm:px-10">
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {[kpi1, kpi2].map((k, i) => (
            <motion.div
              key={k.label}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.6, delay: 2.0 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-xl border border-ink-100 bg-ink-100/85 backdrop-blur-sm px-3 sm:px-4 py-2.5"
            >
              <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-500">
                {k.label}
              </p>
              <p className="mt-0.5 text-base sm:text-lg font-semibold tracking-tight text-ink-950">
                {k.value}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  )
}
